import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Inbox, FileText, Send, X, ScrollText } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { PARTNER_DOC_BUCKET, DOC_LABELS, REQUIRED_DOC_KEYS, OPTIONAL_DOC_KEYS, DOC_NUMBER_FIELDS } from '@/lib/partnerApplications';
import type { PartnerDocFiles, PartnerDocKey } from '@/lib/partnerApplications';
import { DocUploadCard } from '@/components/partners/DocUploadCard';
import { DocumentPreviewModal } from '@/components/partners/DocumentPreviewModal';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../../nav';

const EMPTY_FORM = {
  business_name: '',
  business_type: 'pharmacy',
  contact_person: '',
  designation: '',
  email: '',
  phone: '',
  city: '',
  address: '',
  map_link: '',
  monthly_volume: '',
  why_partner: '',
  drug_license_no: '',
  tin_no: '',
  nid_no: '',
  bin_no: '',
  agreement_terms: '',
  agreement_start_date: '',
  agreement_end_date: '',
};

const STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  pending: 'warning',
  forwarded: 'info',
  approved: 'success',
  rejected: 'error',
};
const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending review',
  forwarded: 'Forwarded to ERP',
  approved: 'Approved',
  rejected: 'Cancelled',
};

async function getDocUrl(path: string): Promise<{ url: string | null; error: string | null }> {
  const { data, error } = await supabase.storage.from(PARTNER_DOC_BUCKET).createSignedUrl(path, 300);
  if (error || !data?.signedUrl) return { url: null, error: error?.message ?? 'Failed to open document' };
  return { url: data.signedUrl, error: null };
}

export default function AdminResellerApplicationDetail() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('pending');
  const [rxkRecommendation, setRxkRecommendation] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [documents, setDocuments] = useState<Record<string, string>>({});
  const [pendingFiles, setPendingFiles] = useState<PartnerDocFiles>({});
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [previewDoc, setPreviewDoc] = useState<{ url: string; label: string } | null>(null);

  useEffect(() => {
    if (!applicationId) return;
    supabase
      .from('core_partner_applications')
      .select('*')
      .eq('id', applicationId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setForm({
            business_name: data.business_name ?? '',
            business_type: data.business_type ?? 'pharmacy',
            contact_person: data.contact_person ?? '',
            designation: data.designation ?? '',
            email: data.email ?? '',
            phone: data.phone ?? '',
            city: data.city ?? '',
            address: data.address ?? '',
            map_link: data.map_link ?? '',
            monthly_volume: data.monthly_volume ?? '',
            why_partner: data.why_partner ?? '',
            drug_license_no: data.drug_license_no ?? '',
            tin_no: data.tin_no ?? '',
            nid_no: data.nid_no ?? '',
            bin_no: data.bin_no ?? '',
            agreement_terms: data.agreement_terms ?? '',
            agreement_start_date: data.agreement_start_date ?? '',
            agreement_end_date: data.agreement_end_date ?? '',
          });
          setDocuments((data.documents as Record<string, string>) ?? {});
          setStatus(data.status);
          setRxkRecommendation(data.rxk_recommendation ?? null);
          setRejectionReason(data.rejection_reason ?? null);
        } else {
          setNotFound(true);
        }
        setLoading(false);
      });
  }, [applicationId]);

  const isEditable = status === 'pending';

  // Date inputs submit '' when cleared, which a `date` column rejects —
  // null is the only valid "no date" value there.
  const formPayload = () => ({
    ...form,
    agreement_start_date: form.agreement_start_date || null,
    agreement_end_date: form.agreement_end_date || null,
  });

  const handleView = async (path: string) => {
    const key = Object.entries(documents).find(([, v]) => v === path)?.[0] as PartnerDocKey | undefined;
    const { url, error } = await getDocUrl(path);
    if (error || !url) {
      push('error', error ?? 'Failed to open document');
      return;
    }
    setPreviewDoc({ url, label: key ? DOC_LABELS[key] : 'Document' });
  };

  const uploadPendingFiles = async (): Promise<Record<string, string> | null> => {
    const next = { ...documents };
    for (const [key, file] of Object.entries(pendingFiles)) {
      if (!file) continue;
      const ext = file.name.split('.').pop() || 'bin';
      const path = `${crypto.randomUUID()}/${key}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from(PARTNER_DOC_BUCKET).upload(path, file);
      if (uploadErr) {
        push('error', `Failed to upload ${DOC_LABELS[key as PartnerDocKey]}: ${uploadErr.message}`);
        return null;
      }
      next[key] = path;
    }
    return next;
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!isEditable) return;
    setBusy(true);
    const nextDocuments = await uploadPendingFiles();
    if (!nextDocuments) {
      setBusy(false);
      return;
    }
    const { error } = await supabase.from('core_partner_applications').update({ ...formPayload(), documents: nextDocuments }).eq('id', applicationId);
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    setDocuments(nextDocuments);
    setPendingFiles({});
    push('success', 'Application updated');
  };

  const handleForward = async () => {
    setBusy(true);
    const nextDocuments = await uploadPendingFiles();
    if (!nextDocuments) {
      setBusy(false);
      return;
    }
    const { error } = await supabase
      .from('core_partner_applications')
      .update({ ...formPayload(), documents: nextDocuments, status: 'forwarded', rxk_recommendation: 'approve' })
      .eq('id', applicationId);
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Forwarded to ERP for approval');
    navigate('/admin/resellers');
  };

  const handleRecommendReject = async () => {
    if (!rejectNote.trim()) return;
    setBusy(true);
    const nextDocuments = await uploadPendingFiles();
    if (!nextDocuments) {
      setBusy(false);
      return;
    }
    const { error } = await supabase
      .from('core_partner_applications')
      .update({ ...formPayload(), documents: nextDocuments, status: 'forwarded', rxk_recommendation: 'reject', rejection_reason: rejectNote.trim() })
      .eq('id', applicationId);
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Sent to ERP recommending cancellation');
    navigate('/admin/resellers');
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (notFound) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Application not found" />
      </AppShell>
    );
  }

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <form onSubmit={handleSave} className="space-y-5 pb-10">
        <div className="rounded-2xl bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 p-5 sm:p-6 text-white shadow-pop space-y-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/resellers')}
              className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white cursor-pointer"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-white/15 flex items-center justify-center">
              <Inbox className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-[19px] font-bold leading-tight truncate">{form.business_name || 'Untitled application'}</h1>
                <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>
              </div>
              <p className="text-[12px] text-white/70">
                {isEditable ? 'Verify details with the applicant, complete anything missing, then forward to ERP.' : 'Read-only — already sent on for a decision.'}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {isEditable && (
              <>
                <Button type="button" variant="secondary" onClick={() => setShowRejectBox((s) => !s)} disabled={busy} className="!bg-white/10 !text-white !border-white/20 hover:!bg-white/20">
                  <X className="w-3.5 h-3.5" /> Recommend Rejection
                </Button>
                <Button type="submit" variant="secondary" loading={busy} className="!bg-white/10 !text-white !border-white/20 hover:!bg-white/20">
                  Save Changes
                </Button>
                <Button type="button" onClick={handleForward} loading={busy} className="!bg-none !bg-white !text-primary-800 hover:!bg-white/90 !shadow-none">
                  <Send className="w-3.5 h-3.5" /> Forward for Approval
                </Button>
              </>
            )}
          </div>
        </div>

        {showRejectBox && isEditable && (
          <Card>
            <CardHeader title="Recommend Rejection" icon={<X className="w-4 h-4" />} />
            <CardBody className="space-y-3">
              <Field label="Reason (sent to ERP, applicant won't see this)">
                <Textarea rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="e.g. Couldn't reach the applicant / outside our coverage" />
              </Field>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setShowRejectBox(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button type="button" variant="danger" onClick={handleRecommendReject} loading={busy} disabled={!rejectNote.trim()}>
                  Send to ERP
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {!isEditable && rxkRecommendation && (
          <Card>
            <CardBody className="flex items-center gap-2">
              <span className="text-[12.5px] text-ink-faint">rxk recommendation:</span>
              <Badge tone={rxkRecommendation === 'approve' ? 'success' : 'error'}>{rxkRecommendation === 'approve' ? 'Approve' : 'Reject'}</Badge>
              {rxkRecommendation === 'reject' && rejectionReason && <span className="text-[12px] text-ink-faint">— {rejectionReason}</span>}
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader title="Business Information" icon={<Inbox className="w-4 h-4" />} />
          <CardBody className="space-y-3.5">
            <Field label="Business Name">
              <Input disabled={!isEditable} value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Type">
                <Select disabled={!isEditable} value={form.business_type} onChange={(e) => setForm((f) => ({ ...f, business_type: e.target.value }))}>
                  <option value="pharmacy">Pharmacy</option>
                  <option value="beauty">Beauty</option>
                  <option value="online">Online</option>
                  <option value="distributor">Distributor</option>
                </Select>
              </Field>
              <Field label="City">
                <Input disabled={!isEditable} value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Contact Person">
                <Input disabled={!isEditable} value={form.contact_person} onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))} />
              </Field>
              <Field label="Designation">
                <Input
                  disabled={!isEditable}
                  value={form.designation}
                  onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
                  placeholder="e.g. Owner, Manager, Pharmacist-in-charge"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Phone">
                <Input disabled={!isEditable} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </Field>
              <Field label="Email">
                <Input type="email" disabled={!isEditable} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </Field>
            </div>
            <Field label="Business Address">
              <Input disabled={!isEditable} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            </Field>
            <Field label="Google Maps Link">
              <Input disabled={!isEditable} value={form.map_link} onChange={(e) => setForm((f) => ({ ...f, map_link: e.target.value }))} />
            </Field>
            <Field label="Monthly Volume">
              <Input disabled={!isEditable} value={form.monthly_volume} onChange={(e) => setForm((f) => ({ ...f, monthly_volume: e.target.value }))} />
            </Field>
            <Field label="Why partner?">
              <Textarea rows={3} disabled={!isEditable} value={form.why_partner} onChange={(e) => setForm((f) => ({ ...f, why_partner: e.target.value }))} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Compliance IDs & Documents" icon={<FileText className="w-4 h-4" />} />
          <CardBody className="space-y-5">
            {(Object.keys(DOC_NUMBER_FIELDS) as PartnerDocKey[]).map((key) => {
              const numberField = DOC_NUMBER_FIELDS[key]!;
              return (
                <div key={key} className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 items-start">
                  <Field label={numberField.label}>
                    <Input disabled={!isEditable} value={form[numberField.key]} onChange={(e) => setForm((f) => ({ ...f, [numberField.key]: e.target.value }))} />
                  </Field>
                  <DocUploadCard
                    label={DOC_LABELS[key]}
                    required={(REQUIRED_DOC_KEYS as readonly string[]).includes(key)}
                    file={pendingFiles[key] ?? null}
                    existingPath={documents[key]}
                    onChange={(file) => setPendingFiles((f) => ({ ...f, [key]: file }))}
                    onView={handleView}
                    disabled={!isEditable}
                  />
                </div>
              );
            })}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              {([...REQUIRED_DOC_KEYS, ...OPTIONAL_DOC_KEYS] as PartnerDocKey[])
                .filter((key) => !DOC_NUMBER_FIELDS[key])
                .map((key) => (
                  <DocUploadCard
                    key={key}
                    label={DOC_LABELS[key]}
                    required={(REQUIRED_DOC_KEYS as readonly string[]).includes(key)}
                    file={pendingFiles[key] ?? null}
                    existingPath={documents[key]}
                    onChange={(file) => setPendingFiles((f) => ({ ...f, [key]: file }))}
                    onView={handleView}
                    disabled={!isEditable}
                  />
                ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Agreement" subtitle="Optional — can also be added later" icon={<ScrollText className="w-4 h-4" />} />
          <CardBody className="space-y-3.5">
            <Field label="Terms & Conditions">
              <Textarea
                rows={5}
                disabled={!isEditable}
                value={form.agreement_terms}
                onChange={(e) => setForm((f) => ({ ...f, agreement_terms: e.target.value }))}
                placeholder="Write the terms agreed with this partner…"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Agreement Start Date">
                <Input
                  type="date"
                  disabled={!isEditable}
                  value={form.agreement_start_date}
                  onChange={(e) => setForm((f) => ({ ...f, agreement_start_date: e.target.value }))}
                />
              </Field>
              <Field label="Agreement End Date">
                <Input
                  type="date"
                  disabled={!isEditable}
                  value={form.agreement_end_date}
                  onChange={(e) => setForm((f) => ({ ...f, agreement_end_date: e.target.value }))}
                />
              </Field>
            </div>
          </CardBody>
        </Card>
      </form>
      {previewDoc && <DocumentPreviewModal url={previewDoc.url} label={previewDoc.label} onClose={() => setPreviewDoc(null)} />}
    </AppShell>
  );
}
